import type { EnvironmentId } from "@amoreg/contracts";
import { assertProjectFileSaved } from "./projectFilesQueryState";

// Editors register against the exact workspace file, so every export entry point
// waits for pending binary writes as well as checking optimistic text drafts.
const editors = new Map<string, Set<() => Promise<void>>>();
const pendingWrites = new Map<string, Set<Promise<void>>>();
const failedWrites = new Map<string, { generation: number; error: unknown }>();
const confirmedWrites = new Map<string, number>();
let nextGeneration = 0;

/** A generation belongs to one editor lifetime, including its retries and cleanup. */
export function createWorkspaceFileWriteGeneration(): number {
  return ++nextGeneration;
}
const fileKey = (environmentId: EnvironmentId, cwd: string, path: string) =>
  JSON.stringify([environmentId, cwd, path]);

export function registerWorkspaceFileFlush(
  environmentId: EnvironmentId,
  cwd: string,
  path: string,
  flush: () => Promise<void>,
): () => void {
  const key = fileKey(environmentId, cwd, path);
  const callbacks = editors.get(key) ?? new Set<() => Promise<void>>();
  callbacks.add(flush);
  editors.set(key, callbacks);
  return () => {
    callbacks.delete(flush);
    if (callbacks.size === 0) editors.delete(key);
  };
}

/** Clear a retained failure only after new bytes have reached the workspace. */
export function confirmWorkspaceFileWrite(
  environmentId: EnvironmentId,
  cwd: string,
  path: string,
  generation: number,
): void {
  const key = fileKey(environmentId, cwd, path);
  confirmedWrites.set(key, Math.max(confirmedWrites.get(key) ?? 0, generation));
  const failure = failedWrites.get(key);
  if (failure && failure.generation <= generation) failedWrites.delete(key);
}

/** Keep teardown writes visible after the editor has unregistered. */
export function trackWorkspaceFileWrite(
  environmentId: EnvironmentId,
  cwd: string,
  path: string,
  write: Promise<void>,
  generation: number,
): void {
  const key = fileKey(environmentId, cwd, path);
  const pending = pendingWrites.get(key) ?? new Set<Promise<void>>();
  // Handle rejection here as well as in export preparation: closing an editor
  // without exporting must not produce an unhandled rejection.
  // Settling without a write (for example, closing a clean viewer) must not
  // clear a previous failure. Only confirmWorkspaceFileWrite does that.
  const tracked = write
    .catch((error: unknown) => {
      const failure = failedWrites.get(key);
      if (
        generation >= (confirmedWrites.get(key) ?? 0) &&
        generation >= (failure?.generation ?? 0)
      ) {
        failedWrites.set(key, { generation, error });
      }
    })
    .finally(() => {
      pending.delete(tracked);
      if (pending.size === 0) pendingWrites.delete(key);
    });
  pending.add(tracked);
  pendingWrites.set(key, pending);
}

export async function prepareWorkspaceFileExport(
  environmentId: EnvironmentId,
  cwd: string,
  path: string,
): Promise<void> {
  const key = fileKey(environmentId, cwd, path);
  for (const flush of editors.get(key) ?? []) await flush();
  while (pendingWrites.has(key)) await Promise.all(pendingWrites.get(key)!);
  if (failedWrites.has(key)) throw failedWrites.get(key)!.error;
  assertProjectFileSaved(environmentId, cwd, path);
}
